import { ISGLayerIds } from "fond/types/layer";

import { ValidationFunction } from "./GenericValidator";

/**
 * Validation function for ISG demand points.
 *
 * A demand point is valid if:
 * - It has at least one leadIn (LineString) connected to it
 *
 * When a demand point is moved, this validation will be triggered for:
 * - The demand point itself
 * - Any leadIn features that were connected before the move
 * - Any leadIn features that are connected after the move
 */
export const validateDemand: ValidationFunction = (feature, allFeatures, helpers) => {
  const featureId = feature.id as string;
  const errors: string[] = [];

  // Get the demand point's position
  if (feature.geometry.type !== "Point") {
    return {
      featureId,
      valid: false,
      errors: ["Demand must be a Point feature"],
    };
  }

  const demandPosition = feature.geometry.coordinates;

  // Find all leadIn features colocated at this demand point
  const colocatedLeadIns = helpers.findFeaturesAtPosition(demandPosition, ISGLayerIds.leadins);

  // Check if any leadIn has EITHER end at this demand point
  const connectedLeadIns = colocatedLeadIns.filter((leadIn) => {
    if (leadIn.geometry.type !== "LineString") return false;

    const coords = leadIn.geometry.coordinates;
    const firstVertex = coords[0];
    const lastVertex = coords[coords.length - 1];

    const firstColocated = helpers.isColocated(demandPosition, firstVertex);
    const lastColocated = helpers.isColocated(demandPosition, lastVertex);

    // Either end can be connected to the demand
    return firstColocated || lastColocated;
  });

  if (connectedLeadIns.length === 0) {
    errors.push("Demand point must have at least one leadIn connected to it");
  }

  return {
    featureId,
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Validation function for ISG leadIn features.
 *
 * A leadIn is valid if:
 * - One end (either vertex) is colocated with a demand point
 * - The other end is colocated with a ugPoint
 * - The order doesn't matter
 *
 * When a leadIn is moved, this validation will be triggered for:
 * - The leadIn itself
 * - Any demand/ugPoint features that were connected before the move
 * - Any demand/ugPoint features that are connected after the move
 */
export const validateLeadIn: ValidationFunction = (feature, allFeatures, helpers) => {
  const featureId = feature.id as string;
  const errors: string[] = [];

  if (feature.geometry.type !== "LineString") {
    return {
      featureId,
      valid: false,
      errors: ["LeadIn must be a LineString feature"],
    };
  }

  const coords = feature.geometry.coordinates;

  if (coords.length < 2) {
    errors.push("LeadIn must have at least 2 vertices");
    return {
      featureId,
      valid: false,
      errors,
    };
  }

  const firstVertex = coords[0];
  const lastVertex = coords[coords.length - 1];

  // Check which points are at each end
  const firstAtDemand = helpers.findFeaturesAtPosition(firstVertex, ISGLayerIds.demand).length > 0;
  const firstAtUgPoint = helpers.findFeaturesAtPosition(firstVertex, ISGLayerIds.ugPoints).length > 0;

  const lastAtDemand = helpers.findFeaturesAtPosition(lastVertex, ISGLayerIds.demand).length > 0;
  const lastAtUgPoint = helpers.findFeaturesAtPosition(lastVertex, ISGLayerIds.ugPoints).length > 0;

  // Valid if one end is at demand and the other is at ugPoint (order doesn't matter)
  const validConfiguration = (firstAtDemand && lastAtUgPoint) || (firstAtUgPoint && lastAtDemand);

  if (!validConfiguration) {
    if (!firstAtDemand && !lastAtDemand) {
      errors.push("LeadIn must have one end connected to a demand point");
    }
    if (!firstAtUgPoint && !lastAtUgPoint) {
      errors.push("LeadIn must have one end connected to a ugPoint");
    }
    if ((firstAtDemand && lastAtDemand) || (firstAtUgPoint && lastAtUgPoint)) {
      errors.push("LeadIn must connect a demand point to a ugPoint (not two of the same type)");
    }
  }

  return {
    featureId,
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Validation function for ISG ugPoint features.
 *
 * A ugPoint is valid if:
 * - It has at least one leadIn or ugPath connected to it
 */
export const validateUgPoint: ValidationFunction = (feature, allFeatures, helpers) => {
  const featureId = feature.id as string;
  const errors: string[] = [];

  if (feature.geometry.type !== "Point") {
    return {
      featureId,
      valid: false,
      errors: ["ugPoint must be a Point feature"],
    };
  }

  const ugPointPosition = feature.geometry.coordinates;

  // Find all leadIns and ugPaths colocated at this ugPoint
  const colocatedLeadIns = helpers.findFeaturesAtPosition(ugPointPosition, ISGLayerIds.leadins);
  const colocatedUgPaths = helpers.findFeaturesAtPosition(ugPointPosition, ISGLayerIds.ugPath);

  // Check if any leadIn has EITHER end at this ugPoint
  const connectedLeadIns = colocatedLeadIns.filter((leadIn) => {
    if (leadIn.geometry.type !== "LineString") return false;

    const coords = leadIn.geometry.coordinates;
    const firstVertex = coords[0];
    const lastVertex = coords[coords.length - 1];

    const firstColocated = helpers.isColocated(ugPointPosition, firstVertex);
    const lastColocated = helpers.isColocated(ugPointPosition, lastVertex);

    // Either end can be connected to the ugPoint
    return firstColocated || lastColocated;
  });

  // Check if any ugPath has any vertex at this ugPoint
  const connectedUgPaths = colocatedUgPaths.filter((ugPath) => {
    if (ugPath.geometry.type !== "LineString") return false;

    return ugPath.geometry.coordinates.some((vertex) => helpers.isColocated(ugPointPosition, vertex));
  });

  const totalConnections = connectedLeadIns.length + connectedUgPaths.length;

  if (totalConnections === 0) {
    errors.push("ugPoint must have at least one leadIn or ugPath connected to it");
  }

  return {
    featureId,
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Validation function for ISG ugPath features.
 *
 * A ugPath is valid if:
 * - The first vertex is colocated with a ugPoint
 * - The last vertex is colocated with a ugPoint
 *
 * When a ugPath is moved, this validation will be triggered for:
 * - The ugPath itself
 * - Any ugPoint features that were connected before the move
 * - Any ugPoint features that are connected after the move
 */
export const validateUgPath: ValidationFunction = (feature, _allFeatures, helpers) => {
  const featureId = feature.id as string;
  const errors: string[] = [];

  if (feature.geometry.type !== "LineString") {
    return {
      featureId,
      valid: false,
      errors: ["ugPath must be a LineString feature"],
    };
  }

  const coords = feature.geometry.coordinates;

  if (coords.length < 2) {
    errors.push("ugPath must have at least 2 vertices");
    return {
      featureId,
      valid: false,
      errors,
    };
  }

  const firstVertex = coords[0];
  const lastVertex = coords[coords.length - 1];

  // Check if both endpoints are colocated with ugPoints
  const firstAtUgPoint = helpers.findFeaturesAtPosition(firstVertex, ISGLayerIds.ugPoints).length > 0;
  const lastAtUgPoint = helpers.findFeaturesAtPosition(lastVertex, ISGLayerIds.ugPoints).length > 0;

  if (!firstAtUgPoint) {
    errors.push("ugPath first vertex must be connected to a ugPoint");
  }
  if (!lastAtUgPoint) {
    errors.push("ugPath last vertex must be connected to a ugPoint");
  }

  return {
    featureId,
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Example: How to use these validation functions in DataPrepDraw
 *
 * @example
 * import { validateDemand, validateLeadIn, validateUgPoint, validateUgPath } from 'fond/draw/validation/isgValidation';
 *
 * const defaultOptions: MapboxDrawOptions = {
 *   allLayers: [
 *     {
 *       ID: ISGLayerIds.demand,
 *       GeometryType: "Point",
 *       validate: validateDemand,  // Add validation here
 *       getFeatureProperties: () => ({ layerId: ISGLayerIds.demand }),
 *     },
 *     {
 *       ID: ISGLayerIds.leadins,
 *       GeometryType: "LineString",
 *       validate: validateLeadIn,  // Add validation here
 *       options: { midPoints: false },
 *       getFeatureProperties: () => ({ layerId: ISGLayerIds.leadins }),
 *     },
 *     {
 *       ID: ISGLayerIds.ugPoints,
 *       GeometryType: "Point",
 *       validate: validateUgPoint,  // Add validation here
 *       getFeatureProperties: () => ({ layerId: ISGLayerIds.ugPoints }),
 *     },
 *     {
 *       ID: ISGLayerIds.ugPath,
 *       GeometryType: "LineString",
 *       validate: validateUgPath,  // Add validation here
 *       options: { midPoints: false },
 *       getFeatureProperties: () => ({ layerId: ISGLayerIds.ugPath }),
 *     },
 *   ],
 *   // ... other options
 * };
 */
